import * as React from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { getBackendHost } from '@/lib/api-client';

type Provider = { alias: string; display_name: string };

const MARKS: Record<string, React.ReactNode> = {
  google: (
    <svg viewBox="0 0 48 48" className="h-4 w-4" aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.3 13.2 17.7 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.1 24.6c0-1.6-.1-3.2-.4-4.6H24v9.1h12.4c-.5 2.9-2.1 5.4-4.6 7l7.1 5.5c4.2-3.8 6.6-9.5 6.6-17z" />
      <path fill="#FBBC05" d="M10.4 28.7a14.5 14.5 0 0 1 0-9.4l-7.8-6.1a24 24 0 0 0 0 21.6l7.8-6.1z" />
      <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.1-5.5c-2 1.3-4.6 2.1-8.8 2.1-6.3 0-11.7-3.7-13.6-9.1l-7.8 6.1C6.5 42.6 14.6 48 24 48z" />
    </svg>
  ),
  microsoft: (
    <svg viewBox="0 0 23 23" className="h-4 w-4" aria-hidden>
      <path fill="#F25022" d="M1 1h10v10H1z" />
      <path fill="#7FBA00" d="M12 1h10v10H12z" />
      <path fill="#00A4EF" d="M1 12h10v10H1z" />
      <path fill="#FFB900" d="M12 12h10v10H12z" />
    </svg>
  ),
  entraid: (
    <svg viewBox="0 0 23 23" className="h-4 w-4" aria-hidden>
      <path fill="#0078D4" d="M11.5 1 2 20h6l3.5-7.4L15 20h6z" />
    </svg>
  ),
  okta: (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden>
      <circle cx="12" cy="12" r="10" fill="#007DC1" />
      <circle cx="12" cy="12" r="4" fill="#fff" />
    </svg>
  ),
};

export default function IdentityProviderButtons({
  label = 'or continue with',
  heading,
}: {
  label?: string;
  heading?: string;
}) {
  const { login } = useAuth();
  const [providers, setProviders] = React.useState<Provider[] | null>(null);
  const [busy, setBusy] = React.useState<string | null>(null);

  React.useEffect(() => {
    const base = `https://${getBackendHost(window.location.hostname)}/api/frontend`;
    axios
      .get(`${base}/public/identity-providers/`)
      .then((res) => setProviders(res.data?.providers ?? []))
      .catch(() => setProviders([]));
  }, []);

  if (!providers || providers.length === 0) return null;

  const rule = (
    <div className="flex items-center gap-3">
      <span className="h-px flex-1 bg-border" />
      <span className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="h-px flex-1 bg-border" />
    </div>
  );

  return (
    <div className="space-y-3" data-testid="identity-providers">
      {heading ? (
        <p className="text-xs uppercase tracking-wide text-muted-foreground">
          {heading}
        </p>
      ) : (
        rule
      )}

      <div className="grid gap-2">
        {providers.map((provider) => (
          <Button
            key={provider.alias}
            type="button"
            variant="outline"
            className="w-full justify-center gap-2"
            disabled={busy !== null}
            onClick={() => {
              setBusy(provider.alias);
              login(provider.alias).catch(() => setBusy(null));
            }}
            data-testid={`idp-${provider.alias}`}
          >
            {busy === provider.alias ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              MARKS[provider.alias] ?? null
            )}
            {provider.display_name}
          </Button>
        ))}
      </div>

      {heading ? rule : null}
    </div>
  );
}
